import { win } from "./pd-window.js";

/**
 * Retrieves the active OneTrust consent groups from the global window object.
 * Parses the comma-separated string of consent groups and filters out empty entries.
 *
 * @returns {string[]} An array of active consent group identifiers, or an empty array if no groups are active
 */
const getConsentGroups = () => {
  return win.OnetrustActiveGroups
    ? win.OnetrustActiveGroups.split(",").filter((it) => 0 < it.trim().length)
    : [];
};

/**
 * Checks if a specific consent group is currently active.
 *
 * @param {string} consentGroup - The consent group identifier to check
 * @returns {boolean} True if the consent group is active, false otherwise
 */
const isConsentGroupActive = (consentGroup) => {
  const activeGroups = getConsentGroups();
  return activeGroups.includes(consentGroup);
};

/**
 * Extends the global OptanonWrapper function by wrapping it with additional functionality.
 * Preserves the original OptanonWrapper behavior while adding a custom callback.
 *
 * @param {Function} callback - The callback function to execute after the original OptanonWrapper
 */
const extendOptanonWrapper = (callback) => {
  const originalOptanonWrapper = win.OptanonWrapper;

  // Create new OptanonWrapper that calls both original and new callback
  win.OptanonWrapper = function () {
    // Call the original function first (if it exists)
    if (
      originalOptanonWrapper &&
      typeof originalOptanonWrapper === "function"
    ) {
      originalOptanonWrapper();
    }

    // New functionality
    callback();
  };
};

/**
 * Adds a consent callback that executes when a specific OneTrust consent group is active.
 * If OneTrust is already loaded, the callback is executed immediately (if consent is granted).
 * If OneTrust is not yet loaded, the callback is executed once OneTrust initializes.
 *
 * @param {string} consentGroup - The OneTrust consent group identifier to check
 * @param {Function} callback - The function to execute when the consent group is active
 * @param {Function} [errorCallback] - Optional function to execute when the consent group is not active
 */
export const addConsentCallback = (consentGroup, callback, errorCallback) => {
  // Function to execute the callback if the consent group is active
  const executeCallbackIfConsentActive = () => {
    if (isConsentGroupActive(consentGroup)) {
      callback();
    } else if (errorCallback) {
      errorCallback();
    }
  };

  // Check if OneTrust is active
  if (win.OnetrustActiveGroups) {
    executeCallbackIfConsentActive();
  } else {
    // Wait for OneTrust to load
    extendOptanonWrapper(executeCallbackIfConsentActive);
  }
};
